import { ReactNode, useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { mergeHydratedSpaces, useContextStore } from '@store/context'
import type { Space } from '@/services/api/spaces'
import type { Deployment } from '@/services/api/deployments'
import type { Organization } from '@/services/api/organizations'
import {
  getFixedOrganizationId,
  isConsoleScopedInstance,
} from '@/config/runtime'
import { standaloneSetupAPI } from '@/services/api/standaloneSetup'
import { spacesAPI } from '@/services/api/spaces'
import { applicationsAPI } from '@/services/api/applications'
import { useAuth } from './AuthProvider'
import { selectScopedResources } from './scopedOrganizations'
import {
  isAgentSpecAuthoringPath,
  readApplicationKey,
  readContextTargetId,
  reconcileContextSearch,
  resolveUrlSyncAgentId,
  resolveUrlSyncApplicationKey,
} from '@/utils/contextUrl'
import { isLeafNavigationLocked } from '@/utils/leafNavigationLock'
import { routePosture, usesApplicationContext } from '@/utils/posture'
import {
  applicationsVisibleInRun,
  humanMayAccessApplication,
  resolveRunChatAgentId,
  runAgentsForApplication,
} from '@/components/shell/runApplicationAgents'
import {
  isChiefOfStaffDeployment,
  resolveBootstrapHostId,
  resolvePreferredStudioHost,
  splitBusinessDeployments,
} from '@/utils/instanceRole'

const STORAGE_KEYS = {
  org: 'agent-os.current-org-id',
  deployment: 'agent-os.current-deployment-id',
  workspace: 'agent-os.current-workspace-id',
  application: 'agent-os.current-application-key',
}

const URL_KEYS = {
  org: 'organizationId',
}

function getSpaceOrgId(space: Space): string | null {
  const candidate = (space as Space & {
    organizationId?: string
    organization_id?: string
  })

  return candidate.orgId || candidate.organizationId || candidate.organization_id || null
}

function isSpaceInOrganization(space: Space, orgId: string): boolean {
  const spaceOrgId = getSpaceOrgId(space)
  if (!spaceOrgId) {
    return true
  }
  return spaceOrgId === orgId
}

function getSpaceDeploymentIds(space: Space): string[] {
  const candidate = space as Space & {
    deploymentId?: string
    deployment_id?: string
    deploymentIds?: string[]
    deployment_ids?: string[]
  }

  const ids = [
    ...(Array.isArray(candidate.deploymentIds) ? candidate.deploymentIds : []),
    ...(Array.isArray(candidate.deployment_ids) ? candidate.deployment_ids : []),
  ]

  const single =
    candidate.deploymentId || candidate.deployment_id || null
  if (single) {
    ids.push(single)
  }

  return [...new Set(ids.filter(Boolean))]
}

function isSpaceInDeployment(space: Space, deploymentId: string): boolean {
  const deploymentIds = getSpaceDeploymentIds(space)
  if (deploymentIds.length === 0) {
    return true
  }
  return deploymentIds.includes(deploymentId)
}

function sameIdList<T extends { id: string }>(left: T[], right: T[]) {
  if (left.length !== right.length) {
    return false
  }

  return left.every((item, index) => item.id === right[index]?.id)
}

function sameOrganizations(left: Organization[], right: Organization[]) {
  return sameIdList(left, right)
}

function sameDeployments(left: Deployment[], right: Deployment[]) {
  return sameIdList(left, right)
}

function sameSpaces(left: Space[], right: Space[]) {
  if (!sameIdList(left, right)) {
    return false
  }

  // Also refresh when session names / membership evolve for the same space ids.
  return left.every((space, index) => {
    const other = right[index]
    if (!other) {
      return false
    }
    const leftSessions = space.sessions || []
    const rightSessions = other.sessions || []
    if (leftSessions.length !== rightSessions.length) {
      return false
    }
    return leftSessions.every((session, sessionIndex) => {
      const otherSession = rightSessions[sessionIndex]
      return (
        session.id === otherSession?.id &&
        (session.name || '') === (otherSession?.name || '')
      )
    })
  })
}

function buildFallbackDeployment(
  orgId: string,
  deploymentId: string,
  name?: string | null,
): Deployment {
  const now = new Date().toISOString()
  return {
    id: deploymentId,
    name: name?.trim() || 'Assistant',
    orgId,
    runtimeTarget: 'vm',
    status: 'active',
    version: '0.0.0',
    recipes: [],
    createdAt: now,
    updatedAt: now,
  }
}

interface ContextProviderProps {
  children: ReactNode
  defaultOrg?: string
  defaultEnv?: string
  defaultWorkspace?: string
}

export function ContextProvider({
  children,
  defaultOrg,
  defaultEnv,
  defaultWorkspace,
}: ContextProviderProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { loading: authLoading, isAuthenticated, organizations, user } = useAuth()
  const org = useContextStore((state) => state.org)
  const deploymentId = useContextStore((state) => state.deploymentId)
  const workspace = useContextStore((state) => state.workspace)
  const setOrganizations = useContextStore((state) => state.setOrganizations)
  const setDeployments = useContextStore((state) => state.setDeployments)
  const setChiefOfStaff = useContextStore((state) => state.setChiefOfStaff)
  const setApplications = useContextStore((state) => state.setApplications)
  const setSpaces = useContextStore((state) => state.setSpaces)
  const setOrg = useContextStore((state) => state.setOrg)
  const setDeploymentId = useContextStore((state) => state.setDeploymentId)
  const setApplicationKey = useContextStore((state) => state.setApplicationKey)
  const setWorkspace = useContextStore((state) => state.setWorkspace)
  const setLoading = useContextStore((state) => state.setLoading)
  const setError = useContextStore((state) => state.setError)
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  )
  const scopedInstance = useMemo(() => isConsoleScopedInstance(), [])
  const fixedOrganizationId = useMemo(() => getFixedOrganizationId(), [])
  const urlOrgId = searchParams.get(URL_KEYS.org)
  const urlContextTargetId = readContextTargetId(location.pathname, searchParams)
  const urlApplicationKey = readApplicationKey(searchParams)

  useEffect(() => {
    let cancelled = false

    const bootstrapOrganizations = () => {
      if (authLoading) {
        setLoading(true)
        return
      }

      if (!isAuthenticated) {
        setOrganizations([])
        setOrg(null)
        setLoading(false)
        return
      }

      setLoading(true)
      setError(null)

      try {
        const orgs = organizations.map((membership) => membership.organization)
        const availableOrganizations = selectScopedResources(
          orgs,
          (item) => item.id,
          scopedInstance,
          fixedOrganizationId,
        )
        if (cancelled) return

        const currentState = useContextStore.getState()
        if (!sameOrganizations(currentState.organizations, availableOrganizations)) {
          setOrganizations(availableOrganizations)
        }

        const storedOrgId = localStorage.getItem(STORAGE_KEYS.org)
        const preferredOrgId =
          scopedInstance
            ? availableOrganizations.find((item) => item.id === fixedOrganizationId)?.id ||
              availableOrganizations[0]?.id ||
              null
            : urlOrgId || defaultOrg || storedOrgId
        const nextOrg =
          availableOrganizations.find((item) => item.id === preferredOrgId) ||
          availableOrganizations[0] ||
          null

        const nextOrgId = nextOrg?.id || null
        if (currentState.org !== nextOrgId) {
          setOrg(nextOrgId)
        }
      } catch (error) {
        if (cancelled) return
        setOrganizations([])
        setOrg(defaultOrg || null)
        setError(error instanceof Error ? error.message : 'Failed to load organizations')
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    bootstrapOrganizations()

    return () => {
      cancelled = true
    }
  }, [
    authLoading,
    defaultOrg,
    fixedOrganizationId,
    isAuthenticated,
    organizations,
    scopedInstance,
    setError,
    setLoading,
    setOrg,
    setOrganizations,
    urlOrgId,
  ])

  useEffect(() => {
    let cancelled = false

    const bootstrapResources = async () => {
      if (authLoading || !isAuthenticated) {
        setDeployments([])
        setChiefOfStaff(null)
        setSpaces([])
        setDeploymentId(null)
        setWorkspace(defaultWorkspace || null)
        return
      }

      const effectiveOrg = org

      if (!effectiveOrg) {
        setDeployments([])
        setChiefOfStaff(null)
        setSpaces([])
        setDeploymentId(null)
        setWorkspace(defaultWorkspace || null)
        return
      }

      setLoading(true)
      setError(null)

      try {
        const selectedMembership =
          organizations.find((membership) => membership.organization.id === effectiveOrg) ||
          null
        if (cancelled) return

        const visibleMemberships = (selectedMembership?.deployments || []).filter(
          (item) => item.isAdmin !== true,
        )
        let { deployments, chiefOfStaff } =
          splitBusinessDeployments(visibleMemberships)
        // Keep an optimistically-added deployment (just created, not yet in
        // the refreshed memberships) so the UI doesn't lose its label.
        const liveStateForOptimistic = useContextStore.getState()
        const optimisticSelection =
          liveStateForOptimistic.deployments.find(
            (item) =>
              item.id === liveStateForOptimistic.deploymentId &&
              !deployments.some((known) => known.id === item.id),
          ) ||
          (liveStateForOptimistic.chiefOfStaff?.id ===
            liveStateForOptimistic.deploymentId &&
          !chiefOfStaff
            ? liveStateForOptimistic.chiefOfStaff
            : null)
        if (optimisticSelection) {
          if (isChiefOfStaffDeployment(optimisticSelection)) {
            chiefOfStaff = chiefOfStaff ?? optimisticSelection
          } else {
            deployments = [...deployments, optimisticSelection]
          }
        }
        let allSpaces = selectedMembership?.spaces || []
        let orgSpaces = allSpaces.filter((space) => isSpaceInOrganization(space, effectiveOrg))

        if (!selectedMembership && scopedInstance) {
          const status = await standaloneSetupAPI
            .getStatus({
              organizationId: effectiveOrg,
              deploymentId: urlContextTargetId || undefined,
            })
            .catch(() => null)
          if (cancelled) return

          const fallbackOrgId = status?.organizationId || effectiveOrg
          const fallbackDeploymentId = status?.deploymentId || null
          if (fallbackOrgId && fallbackDeploymentId) {
            deployments = [
              buildFallbackDeployment(
                fallbackOrgId,
                fallbackDeploymentId,
                status?.deploymentName,
              ),
            ]
            allSpaces = await spacesAPI
              .listByDeployment(fallbackDeploymentId, fallbackOrgId)
              .catch(() => [])
            orgSpaces = allSpaces.filter((space) => isSpaceInOrganization(space, fallbackOrgId))
          }
        }
        // Read the LIVE store state (not the render closure): this effect's
        // closures can lag one render behind, and trusting a stale URL target
        // over the live selection creates an infinite
        // URL <-> store oscillation with the URL-sync effect below.
        const currentState = useContextStore.getState()
        const liveDeploymentId = currentState.deploymentId
        const storedDeploymentId = localStorage.getItem(STORAGE_KEYS.deployment)
        // Prefer an explicit URL target (deep link / E2E) over a stale live
        // selection. Live-first used to ignore ?agentId=/deploymentId= and
        // fall back to deployments[0] (often an Agent without Agent Spec).
        const preferredDeploymentId = resolvePreferredStudioHost({
          pathname: location.pathname,
          urlAgentId: urlContextTargetId,
          chiefOfStaffId: chiefOfStaff?.id || null,
          liveDeploymentId,
          storedDeploymentId,
        })

        const catalog = chiefOfStaff
          ? [...deployments, chiefOfStaff]
          : deployments

        const fallbackDeploymentId =
          (
            deployments.find((item) => item.environment === defaultEnv) ||
            deployments[0] ||
            null
          )?.id || null
        let nextDeploymentId = resolveBootstrapHostId({
          preferredId: preferredDeploymentId,
          liveId: liveDeploymentId,
          fallbackId: fallbackDeploymentId,
        })
        const nextDeployment =
          catalog.find((item) => item.id === nextDeploymentId) || null

        let scopedSpaces = nextDeployment
          ? orgSpaces.filter((space) => isSpaceInDeployment(space, nextDeployment.id))
          : orgSpaces

        try {
          const apps = await applicationsAPI.list(org)
          if (!cancelled) {
            setApplications(apps)
            const storedAppKey = localStorage.getItem(STORAGE_KEYS.application)
            const requireApplication = usesApplicationContext(
              routePosture(location.pathname),
            )
            const catalog = requireApplication
              ? applicationsVisibleInRun(apps, user?.id)
              : apps
            const preferredAppKey =
              urlApplicationKey ||
              currentState.applicationKey ||
              storedAppKey ||
              (requireApplication ? catalog[0]?.key || null : null)
            const urlDenied = Boolean(
              requireApplication &&
                urlApplicationKey &&
                apps.some((item) => item.key === urlApplicationKey) &&
                !humanMayAccessApplication(
                  apps.find((item) => item.key === urlApplicationKey),
                  user?.id,
                ),
            )
            const matchedApp = preferredAppKey
              ? catalog.find((item) => item.key === preferredAppKey) || null
              : null
            const nextAppKey = urlDenied
              ? urlApplicationKey
              : matchedApp?.key ||
                (requireApplication ? catalog[0]?.key || null : null)
            if (currentState.applicationKey !== nextAppKey) {
              setApplicationKey(nextAppKey)
            }
            if (nextAppKey) {
              localStorage.setItem(STORAGE_KEYS.application, nextAppKey)
            } else if (!requireApplication) {
              localStorage.removeItem(STORAGE_KEYS.application)
            }
            // Run: keep the selected Agent when it belongs to the Application.
            if (
              usesApplicationContext(routePosture(location.pathname)) &&
              matchedApp
            ) {
              nextDeploymentId = resolveRunChatAgentId({
                app: matchedApp,
                deployments,
                selectedAgentId: nextDeploymentId,
              })
              const runAgentIds = new Set(
                runAgentsForApplication(matchedApp, deployments).map(
                  (agent) => agent.id,
                ),
              )
              scopedSpaces =
                runAgentIds.size === 0
                  ? orgSpaces
                  : orgSpaces.filter((space) => {
                      if (
                        !space.deploymentId &&
                        (!space.deploymentIds ||
                          space.deploymentIds.length === 0)
                      ) {
                        return true
                      }
                      return [...runAgentIds].some((agentId) =>
                        isSpaceInDeployment(space, agentId),
                      )
                    })
            }
          }
        } catch {
          if (!cancelled) {
            setApplications([])
          }
        }

        if (!sameDeployments(currentState.deployments, deployments)) {
          setDeployments(deployments)
        }
        if (currentState.chiefOfStaff?.id !== (chiefOfStaff?.id || null)) {
          setChiefOfStaff(chiefOfStaff)
        }
        if (!sameSpaces(currentState.spaces, scopedSpaces)) {
          setSpaces(mergeHydratedSpaces(currentState.spaces, scopedSpaces))
        }
        if (currentState.deploymentId !== nextDeploymentId) {
          setDeploymentId(nextDeploymentId)
        }

        const storedWorkspaceId = localStorage.getItem(STORAGE_KEYS.workspace)
        const preferredWorkspaceId =
          currentState.workspace || defaultWorkspace || storedWorkspaceId
        const preferredWorkspace =
          scopedSpaces.find((item) => item.id === preferredWorkspaceId) ||
          null
        const preferredUsableForAgent =
          preferredWorkspace &&
          (!nextDeploymentId ||
            isSpaceInDeployment(preferredWorkspace, nextDeploymentId))
        const nextWorkspace =
          (preferredUsableForAgent ? preferredWorkspace : null) ||
          (nextDeploymentId
            ? scopedSpaces.find((item) =>
                isSpaceInDeployment(item, nextDeploymentId),
              )
            : null) ||
          scopedSpaces[0] ||
          null

        const nextWorkspaceId = nextWorkspace?.id || null
        if (currentState.workspace !== nextWorkspaceId) {
          setWorkspace(nextWorkspaceId)
        }
      } catch (error) {
        if (cancelled) return
        setDeployments([])
        setSpaces([])
        setDeploymentId(null)
        setWorkspace(defaultWorkspace || null)
        setError(error instanceof Error ? error.message : 'Failed to load context resources')
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void bootstrapResources()

    return () => {
      cancelled = true
    }
  }, [
    authLoading,
    defaultEnv,
    defaultWorkspace,
    fixedOrganizationId,
    isAuthenticated,
    org,
    organizations,
    scopedInstance,
    setDeploymentId,
    setDeployments,
    setChiefOfStaff,
    setApplications,
    setApplicationKey,
    setError,
    setLoading,
    setSpaces,
    setWorkspace,
    location.pathname,
    urlApplicationKey,
    urlContextTargetId,
    user?.id,
  ])

  const applicationKey = useContextStore((state) => state.applicationKey)

  const persistedValues = useMemo(
    () => ({
      org,
      deploymentId,
      workspace,
      applicationKey,
    }),
    [applicationKey, deploymentId, org, workspace],
  )

  useEffect(() => {
    if (persistedValues.org) {
      localStorage.setItem(STORAGE_KEYS.org, persistedValues.org)
    } else {
      localStorage.removeItem(STORAGE_KEYS.org)
    }

    if (persistedValues.deploymentId) {
      localStorage.setItem(STORAGE_KEYS.deployment, persistedValues.deploymentId)
    } else {
      localStorage.removeItem(STORAGE_KEYS.deployment)
    }

    if (persistedValues.workspace) {
      localStorage.setItem(STORAGE_KEYS.workspace, persistedValues.workspace)
    } else {
      localStorage.removeItem(STORAGE_KEYS.workspace)
    }

    if (persistedValues.applicationKey) {
      localStorage.setItem(STORAGE_KEYS.application, persistedValues.applicationKey)
    } else {
      localStorage.removeItem(STORAGE_KEYS.application)
    }
  }, [persistedValues])

  useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    // Read the LIVE url, not the render closure: this effect can execute
    // right after a navigation initiated elsewhere in the same commit
    // (e.g. right after creating a deployment). Rebuilding the url from a
    // stale pathname/search used to cancel that navigation and could
    // sustain an infinite replace loop.
    const {
      pathname: livePathname,
      search: liveSearch,
      hash: liveHash,
    } = window.location
    // A LeafLink navigation updates window.location before React's
    // location hook. Rewriting the stale React path (or the still-current
    // chat URL) cancels that in-flight leaf.
    if (location.pathname !== livePathname) {
      return
    }
    if (isLeafNavigationLocked()) {
      return
    }
    const effectiveOrg = org
    const liveState = useContextStore.getState()
    const liveParams = new URLSearchParams(liveSearch)
    const urlAgentId = readContextTargetId(livePathname, liveParams)
    const { changed, search: nextSearch } = reconcileContextSearch({
      pathname: livePathname,
      search: liveSearch,
      organizationId: effectiveOrg,
      agentId: resolveUrlSyncAgentId(urlAgentId, liveState.deploymentId),
      applicationKey: resolveUrlSyncApplicationKey(
        livePathname,
        readApplicationKey(liveParams),
        liveState.applicationKey,
      ),
    })

    if (!changed) {
      return
    }

    navigate(
      {
        pathname: livePathname,
        search: nextSearch ? `?${nextSearch}` : '',
        hash: liveHash,
      },
      { replace: true },
    )
  }, [
    applicationKey,
    deploymentId,
    fixedOrganizationId,
    isAuthenticated,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    org,
    scopedInstance,
  ])

  return <>{children}</>
}
